/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer } from "./ChartContainer";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useAppDispatch, useAppSelector } from "@redux/hooks";

// import { setInterval, setTimeRange } from "@redux/slices/kontrolEdge/portSlice";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import {
  fetchConnectionPerformance,
  setInterval,
  setTimeRange,
} from "@redux/slices/kontrolEdge/connectionSlice";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { ReactComponent as IconInfo } from "../../../../../shared/assets/Icons/circle-info-solid.svg";

// Define the structure for individual bandwidth items
interface BandwidthItem {
  time: string;
  value: number;
}

// Define the structure for the bandwidth utilization
interface BandwidthUtilization {
  inbound: BandwidthItem[];
  outbound: BandwidthItem[];
  timeRange: string;
  interval: string;
  unit: string;
}

interface LatencyDetail {
  destination: string;
  timestamp: string;
  unit: string;
  value: number;
}

// Define the structure for port performance data
interface ConnectionPerformanceData {
  connectionId: number;
  bandwidthUtilization: BandwidthUtilization;
  latencyReadings: LatencyDetail[];
}

interface BandwidthChartProps {
  connectionPerformance: ConnectionPerformanceData | null;
}

//The main list of intervals and time range
const TIME_RANGE_INTERVALS = [
  {
    timeRange: "5m",
    changeIntervals: { high: "5s", medium: "10s", low: "15s" },
  },
  {
    timeRange: "10m",
    changeIntervals: { high: "5s", medium: "10s", low: "20s" },
  },
  {
    timeRange: "30m",
    changeIntervals: { high: "10s", medium: "20s", low: "30s" },
  },
  {
    timeRange: "1h",
    changeIntervals: { high: "20s", medium: "30s", low: "1m" },
  },
  { timeRange: "6h", changeIntervals: { high: "2m", medium: "3m", low: "5m" } },
  {
    timeRange: "12h",
    changeIntervals: { high: "5m", medium: "7m", low: "10m" },
  },
  {
    timeRange: "1d",
    changeIntervals: { high: "7m", medium: "10m", low: "15m" },
  },
  { timeRange: "7d", changeIntervals: { high: "1h", medium: "2h", low: "3h" } },
  {
    timeRange: "15d",
    changeIntervals: { high: "2h", medium: "3h", low: "4h" },
  },
  {
    timeRange: "30d",
    changeIntervals: { high: "4h", medium: "6h", low: "8h" },
  },
  {
    timeRange: "60d",
    changeIntervals: { high: "8h", medium: "12h", low: "24h" },
  },
] as const;

type TimeRangeKey = (typeof TIME_RANGE_INTERVALS)[number]["timeRange"];
type ChangeBuckets = (typeof TIME_RANGE_INTERVALS)[number]["changeIntervals"];

//Helper to turn "5m"→"5 Minutes", "1h30m"→"1 Hour 30 Minutes", etc.
function formatDuration(dur: string): string {
  const parts: string[] = [];
  const regex = /(\d+)(d|h|m|s)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(dur)) !== null) {
    const [_, num, unit] = match;
    let label: string;
    switch (unit) {
      case "d":
        label = num === "1" ? "1 Day" : `${num} Days`;
        break;
      case "h":
        label = num === "1" ? "1 Hour" : `${num} Hours`;
        break;
      case "m":
        label = num === "1" ? "1 Minute" : `${num} Minutes`;
        break;
      case "s":
        label = num === "1" ? "1 Second" : `${num} Seconds`;
        break;
      default:
        label = `${num}${unit}`;
    }
    parts.push(label);
  }
  return parts.join(" ");
}

//Quick lookup map
const CHANGE_INTERVALS_MAP: Record<TimeRangeKey, ChangeBuckets> =
  TIME_RANGE_INTERVALS.reduce((acc, { timeRange, changeIntervals }) => {
    acc[timeRange] = changeIntervals;
    return acc;
  }, {} as any);

//Derive time‐range dropdown options
const TIME_RANGE_OPTIONS = TIME_RANGE_INTERVALS.map(({ timeRange }) => ({
  value: timeRange,
  label: formatDuration(timeRange),
}));

function processData(performanceData: ConnectionPerformanceData | null) {
  // If the performanceData is null or incomplete, return an empty array
  if (!performanceData || !performanceData.bandwidthUtilization) return [];
  const { inbound, outbound } = performanceData.bandwidthUtilization;
  return inbound.map((item: BandwidthItem, index: number) => {
    const date = new Date(item.time);
    // Convert UTC to Mexico City time
    const formattedTime = date.toLocaleString("en-US", {
      timeZone: "America/Mexico_City",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return {
      time: item.time,
      inbound: item.value,
      outbound: outbound[index].value,
      formattedTime,
    };
  });
}

export default function InternetBandwidthChart({
  connectionId,
}: {
  connectionId: string;
}) {
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const {
    timeRange,
    interval,
    performanceData,
    performanceLoading,
    performanceError,
  } = useAppSelector((state) => state.connection);

  useEffect(() => {
    dispatch(
      fetchConnectionPerformance({ api, connectionId, timeRange, interval, tenantId })
    );
  }, [dispatch, connectionId, timeRange, interval]);

  // Local state to control which series to show.
  const [showInbound, setShowInbound] = useState(true);
  const [showOutbound, setShowOutbound] = useState(true);

  // Memoize chart data so it only recalculates when the source changes
  const chartData = useMemo(
    () => processData(performanceData),
    [performanceData]
  );
  const { tenantId } = useAppSelector((state) => state.selection);

  const hasData = chartData.length > 0;
  const hasError = !!performanceError || performanceData === null;

  // Compute which intervals are valid for the current timeRange
  const intervalOptions = useMemo(() => {
    if (!(timeRange in CHANGE_INTERVALS_MAP)) return [];
    const buckets = CHANGE_INTERVALS_MAP[timeRange as TimeRangeKey];
    return (["high", "medium", "low"] as const).map((level) => {
      const value = buckets[level];
      return { value, label: formatDuration(value) };
    });
  }, [timeRange]);

  // Snap the selected interval to the minimum if it falls out of range
  useEffect(() => {
    if (!(timeRange in CHANGE_INTERVALS_MAP)) return;
    const high = CHANGE_INTERVALS_MAP[timeRange as TimeRangeKey].high;
    //Dispatch *once*, on timeRange change:
    if (interval !== high) {
      dispatch(setInterval(high));
    }
  }, [timeRange, dispatch]);

  //Handlers for the dropdowns
  const handleTimeRangeSelect = useCallback(
    (label: string) => {
      //Find the matching timeRange key
      const opt = TIME_RANGE_OPTIONS.find((o) => o.label === label);
      if (!opt) return;
      const key = opt.value as TimeRangeKey;
      const buckets = CHANGE_INTERVALS_MAP[key];
      //Set the timeRange
      dispatch(setTimeRange(key));
      //Snap interval to the minimum ("high") (this avoids the chart freeze)
      dispatch(setInterval(buckets.high));
    },
    [dispatch]
  );

  const handleIntervalSelect = useCallback(
    (label: string) => {
      const opt = intervalOptions.find((o) => o.label === label);
      if (opt) dispatch(setInterval(opt.value));
    },
    [dispatch, intervalOptions]
  );

  return (
    <div className="py-5 space-y-10">
      <GenericCard
        headerContent={
          <h3 className="text-body-medium font-bold">Internet Utilization</h3>
        }
        rightContent={
          <div className="flex content-center">
            <div className="flex flex-col mx-5 space-y-1">
              <div className="h-6 flex items-center justify-between">
                <p className="text-sm">Change Time Range</p>
                {/* empty spacer to match the tooltip width/height */}
                <div className="w-4" />
              </div>
              <GenericDropdown
                items={TIME_RANGE_OPTIONS.map((option) => option.label)}
                value={
                  TIME_RANGE_OPTIONS.find(
                    (option) => option.value === timeRange
                  )?.label || "Select Time Range"
                }
                onSelect={handleTimeRangeSelect}
                defaultLabel="Select Time Range"
                widthClass="w-50"
                maxWidthClass="max-w-[400px]"
                size="sm"
              />
            </div>
            <div className="flex flex-col mx-5 space-y-1">
              <div className="h-6 flex items-center justify-between">
                <p className=" display text-sm content-center">
                  Change Interval
                </p>
                <GenericTooltip text="Change Interval sets how often data is sampled. Shorter intervals (e.g., 5s) show detailed spikes; longer ones (e.g., 1m) smooth the data and highlight trends." />
              </div>
              <GenericDropdown
                items={intervalOptions.map((o) => o.label)}
                value={
                  intervalOptions.find((o) => o.value === interval)?.label ??
                  "Select Interval"
                }
                onSelect={handleIntervalSelect}
                defaultLabel="Select Interval"
                widthClass="w-50"
                maxWidthClass="max-w-[400px]"
                size="sm"
              />
            </div>
            {/* Checkbox Controls for filtering series */}
            <div className="flex gap-4 items-center">
              <GenericCheckbox
                label="Show Inbound"
                checked={showInbound}
                onChange={setShowInbound}
                size="sm"
              />
              <GenericCheckbox
                label="Show Outbound"
                checked={showOutbound}
                onChange={setShowOutbound}
                size="sm"
              />
            </div>
          </div>
        }
        isCollapsible={false}
        bodyContent={
          <>
            <div className="relative h-[300px]">
              {/*Error/no-data placeholder */}
              {!performanceLoading && (hasError || !hasData) && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <IconInfo className="w-8 h-8 text-yellow-500" />
                  <p className="mt-2 text-sm text-semantic-text-secondary">
                    Performance data is unavailable at the moment.
                  </p>
                </div>
              )}

              {/* Chart Container with Loading Overlay */}
              <div className="relative w-90% mx-5">
                <ChartContainer
                  config={{
                    inbound: {
                      label: "Inbound",
                      color: "var(--color-primitive-brand-surf-300)",
                    },
                    outbound: {
                      label: "Outbound",
                      color: "var(--color-primitive-brand-surf-700)",
                    },
                  }}
                  className="h-[300px]"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 30, right: 50, left: 40, bottom: 40 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="formattedTime"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={10}
                        tick={{ fontSize: 12, fill: "#666" }}
                        interval="preserveStartEnd"
                      >
                        <Label
                          value="Time (Mexico City)"
                          position="bottom"
                          offset={20}
                          style={{ textAnchor: "middle", fontSize: 12 }}
                        />
                      </XAxis>
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={10}
                        tick={{ fontSize: 12, fill: "#666" }}
                        tickFormatter={(value) => `${value}`}
                      >
                        <Label
                          value="Mean Bandwidth Usage"
                          angle={-90}
                          position="insideLeft"
                          offset={-23}
                          style={{ textAnchor: "middle", fontSize: 12 }}
                        />
                        <Label
                          value="(Mbps)"
                          angle={-90}
                          position="insideLeft"
                          offset={-5}
                          style={{ textAnchor: "middle", fontSize: 12 }}
                        />
                      </YAxis>
                      <Tooltip />
                      {showInbound && (
                        <Line
                          type="monotone"
                          dataKey="inbound"
                          name="Inbound"
                          stroke="var(--color-inbound)"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      )}
                      {showOutbound && (
                        <Line
                          type="monotone"
                          dataKey="outbound"
                          name="Outbound"
                          stroke="var(--color-outbound)"
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
                {/* Loading overlay as a semi-transparent cover */}
                {performanceLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                    <span className="text-lg font-semibold text-gray-700">
                      Loading…
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end my-5 mr-10">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-300)]" />
                  <span className="text-sm">Inbound</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[var(--color-primitive-brand-surf-700)]" />
                  <span className="text-sm">Outbound</span>
                </div>
              </div>
            </div>
          </>
        }
      />
    </div>
  );
}
